import React, { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, MapPin, Briefcase, Star, Award, Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

const ITALIAN_REGIONS = [
  "Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna",
  "Friuli-Venezia Giulia", "Lazio", "Liguria", "Lombardia", "Marche",
  "Molise", "Piemonte", "Puglia", "Sardegna", "Sicilia",
  "Toscana", "Trentino-Alto Adige", "Umbria", "Valle d'Aosta", "Veneto",
];

const ROLES = [
  "Fractional CEO / General Manager",
  "Fractional COO / Operations Manager",
  "Fractional CFO / Finance Manager",
  "Fractional CTO / Digital Transformation Manager",
  "Fractional CMO / Marketing & Sales Manager",
  "Fractional CHRO / HR & Organization Manager",
  "Fractional CSO / Strategy Manager",
];

export default function CompanyBrowseManagers() {
  const { user } = useAuth();
  const [managers, setManagers] = useState([]);
  const [filters, setFilters] = useState({ role: "", region: "", skill: "" });
  const [loading, setLoading] = useState(true);

  const isPro = user?.subscription_tier === "marketplace_pro" || user?.subscription_tier === "agency_premium";

  const fetchManagers = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.role) params.set("role", filters.role);
    if (filters.region) params.set("region", filters.region);
    if (filters.skill) params.set("skill", filters.skill);
    try {
      const r = await api.get(`/marketplace/managers?${params.toString()}&limit=48`);
      setManagers(r.data?.managers || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchManagers(); }, []);

  return (
    <div>
      <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
        <Briefcase size={24} className="text-[#2D6BFF]" /> Browse Manager
      </h1>
      <p className="mt-1 text-slate-600 text-sm mb-6">Esplora il network CXOORA. Solo manager verificati visibili in Marketplace.</p>

      {!isPro && (
        <div className="cxo-card p-4 mb-5 bg-amber-50 border-l-4 border-l-amber-500 flex items-start gap-3" data-testid="upgrade-prompt">
          <Lock size={18} className="text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <h3 className="font-semibold text-amber-900 text-sm">Funzione Premium (Company Pro)</h3>
            <p className="text-xs text-amber-800 mt-0.5">Stai vedendo un&apos;anteprima limitata. Passa a <strong>Company Pro (€199/mese)</strong> per contattare direttamente i manager.</p>
          </div>
          <Link to="/app/subscription"><Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">Upgrade</Button></Link>
        </div>
      )}

      <div className="cxo-card p-4 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Select value={filters.role || "all"} onValueChange={(v) => setFilters({ ...filters, role: v === "all" ? "" : v })}>
            <SelectTrigger><SelectValue placeholder="Ruolo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i ruoli</SelectItem>
              {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.region || "all"} onValueChange={(v) => setFilters({ ...filters, region: v === "all" ? "" : v })}>
            <SelectTrigger><SelectValue placeholder="Regione" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              {ITALIAN_REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Competenza..." value={filters.skill} onChange={(e) => setFilters({ ...filters, skill: e.target.value })} data-testid="browse-skill-input" />
          <Button onClick={fetchManagers} className="bg-[#2D6BFF] text-white gap-1.5" data-testid="browse-search-btn">
            <Search size={14} /> Cerca
          </Button>
        </div>
      </div>

      {loading ? <p className="text-center text-slate-500 py-8">Caricamento...</p> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {managers.map((m) => {
            const photoUrl = m.photo?.download_url ? `${API_BASE}${m.photo.download_url}?inline=true` : null;
            return (
              <div key={m.id} className="cxo-card p-5 hover:shadow-md transition" data-testid={`browse-card-${m.id}`}>
                <div className="flex items-start gap-3">
                  <div className="rounded-full w-12 h-12 flex items-center justify-center bg-gradient-to-br from-[#EAF0FF] to-white border-2 border-[#2D6BFF]/15 text-[#2D6BFF] font-bold text-sm shrink-0">
                    {photoUrl ? <img src={photoUrl} alt="" className="w-full h-full object-cover rounded-full" /> : (m.full_name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{isPro ? m.full_name : "Manager Verified"}</p>
                    <p className="text-xs text-slate-600 truncate">{m.primary_role}</p>
                  </div>
                  {m.is_premium && <Badge className="bg-[#7c3aed]/10 text-[#7c3aed] border-0 text-[10px] shrink-0"><Award size={10} /></Badge>}
                </div>
                <p className="text-xs text-slate-600 mt-2 line-clamp-2 min-h-[32px]">{m.bio_short}</p>
                <div className="mt-3 flex flex-wrap gap-1">
                  {(m.skills || []).slice(0, 3).map((s, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{s}</span>
                  ))}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                  <span className="flex items-center gap-1"><MapPin size={11} className="text-slate-400" />{m.city || "—"}</span>
                  <span className="flex items-center gap-1"><Star size={11} className="text-[#B7791F]" />{m.rating_average?.toFixed(1) || "—"}</span>
                </div>
                {isPro && m.daily_rate_min && (
                  <p className="text-xs text-slate-500 mt-2">€{m.daily_rate_min}-{m.daily_rate_max}/g</p>
                )}
              </div>
            );
          })}
          {!loading && managers.length === 0 && <div className="col-span-3 cxo-card p-10 text-center text-slate-500">Nessun manager trovato.</div>}
        </div>
      )}
    </div>
  );
}
